import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Col, Descriptions, Row, Segmented, Table, Typography } from 'antd'
import {
  DISCARD_LABELS,
  VH_MARKET_LABELS,
  countLine,
  formatUsd,
  groupChoiceMethods,
  outcomeLine,
  percentOf,
  routingRows,
  slotLabel
} from './utils'
import { getRequest, dutyIntakeReportUrl } from '../../../config'
import { carDecisionRows } from '../decisions'
import { serviceLabel } from '../statusColors'
import { labelFor } from '../../../utils/wireKeys'

const { Text, Paragraph } = Typography
const WINDOWS = [7, 30, 90]

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 24 }}>
    <h5 style={{ marginBottom: 8 }}>{title}</h5>
    {children}
  </div>
)

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node
}

const Note = ({ text }) => (text ? <Text type="secondary">{text}</Text> : null)

Note.propTypes = { text: PropTypes.string }


// Whether the intake changes worked, from the API's own numbers — nothing is recomputed here except
// grouping the ways a list was answered (spec: duty-assistant-intake-ux, w-admin Req 3).
const PilotReport = ({ onLoaded }) => {
  const [days, setDays] = useState(30)
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    const load = async () => {
      setError('')
      const res = await getRequest(`${dutyIntakeReportUrl}?days=${days}`, 'pilot report', setLoading)
      if (res.success) {
        setReport(res.data)
        onLoaded(res.data)
      } else {
        setError(
          res.error?.response?.data?.detail ||
            res.error?.response?.data?.error ||
            'The report could not be loaded. The settings above still work.'
        )
      }
    }
    load()
    // eslint-disable-next-line
  }, [days])

  const ad = report?.adArrivals
  const routes = report?.routes
  const cd = report?.carDecisions
  const fc = report?.finalCheck
  const ch = report?.choices
  const ec = report?.entryChecks
  const img = report?.images
  const conv = report?.pictureConversion
  const changes = report?.changes
  const vh = report?.vehicleHistory

  /* eslint-disable react/display-name */
  const choiceColumns = [
    { title: 'Question', dataIndex: 'slot', key: 'slot', render: (_, r) => slotLabel(r.slot) },
    { title: 'Tapped', dataIndex: 'tap', key: 'tap' },
    { title: 'Typed a number', dataIndex: 'number', key: 'number' },
    { title: 'Typed the value', dataIndex: 'value', key: 'value' },
    { title: 'Other', dataIndex: 'other', key: 'other' },
    {
      title: 'Typed instead of tapping',
      key: 'typed',
      render: (_, r) => percentOf(r.typedPct, r.total)
    }
  ]
  /* eslint-enable react/display-name */

  const choiceRows = Object.entries(ch?.bySlot || {}).map(([slot, bucket]) => ({
    slot,
    ...groupChoiceMethods(bucket)
  }))

  return (
    <div className="card mb-5rem">
      <div className="card-body">
        <Row justify="space-between" align="middle" wrap style={{ marginBottom: 16 }} gutter={[8, 8]}>
          <Col>
            <h3 style={{ marginBottom: 0 }}>Pilot report</h3>
          </Col>
          <Col>
            <Segmented
              options={WINDOWS.map(d => ({ label: `${d} days`, value: d }))}
              value={days}
              onChange={setDays}
            />
          </Col>
        </Row>

        {error && <Alert type="warning" showIcon message={error} style={{ marginBottom: 16 }} />}
        {loading && !report && <Text type="secondary">Loading…</Text>}

        {report && (
          <>
            <Section title="Ad arrivals">
              {ad?.arrivals ? (
                <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered>
                  <Descriptions.Item label="Arrivals from the ad">
                    {percentOf(ad.arrivalsPct, ad.conversations)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Sent a second message">
                    {percentOf(ad.sentASecondMessagePct, ad.arrivals)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Paid">{percentOf(ad.paidPct, ad.arrivals)}</Descriptions.Item>
                  <Descriptions.Item label="Follow-ups sent">{ad.nudgesSent}</Descriptions.Item>
                  <Descriptions.Item label="Replied after the follow-up">
                    {percentOf(ad.repliedAfterNudgePct, ad.nudgesSent)}
                  </Descriptions.Item>
                </Descriptions>
              ) : (
                <Note text={ad?.note || 'No ad arrivals in this period.'} />
              )}
            </Section>

            <Section title="Final check">
              <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered>
                <Descriptions.Item label="Questions asked">{fc?.flagged ?? 0}</Descriptions.Item>
                <Descriptions.Item label="Thrown away before a customer saw them">
                  {fc?.discardedTotal ?? 0}
                </Descriptions.Item>
                <Descriptions.Item label="Customer confirmed">
                  {percentOf(fc?.confirmedPct, fc?.flagged)}
                </Descriptions.Item>
                <Descriptions.Item label="Customer corrected">
                  {percentOf(fc?.correctedPct, fc?.flagged)}
                </Descriptions.Item>
                <Descriptions.Item label="No reply">{fc?.noReply ?? 0}</Descriptions.Item>
                <Descriptions.Item label="Checks that failed">{fc?.failed ?? 0}</Descriptions.Item>
              </Descriptions>
              {fc?.discardedTotal > 0 && (
                <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                  Thrown away because they{' '}
                  {Object.entries(fc.discarded)
                    .map(([rule, n]) => `${labelFor(DISCARD_LABELS, rule) || rule} (${n})`)
                    .join(', ')}
                  .
                </Paragraph>
              )}
            </Section>

            <Section title="Numbered choices">
              {choiceRows.length ? (
                <Table
                  size="small"
                  rowKey="slot"
                  columns={choiceColumns}
                  dataSource={choiceRows}
                  pagination={false}
                  scroll={{ x: true }}
                />
              ) : (
                <Note text="No list has been answered in this period." />
              )}
            </Section>

            <Section title="Checked as typed">
              <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered>
                <Descriptions.Item label="Questions asked">{ec?.asked ?? 0}</Descriptions.Item>
                <Descriptions.Item label="Picked one of the car's options">
                  {ec?.picked ?? 0}
                </Descriptions.Item>
                <Descriptions.Item label="Kept their own">{ec?.kept ?? 0}</Descriptions.Item>
                <Descriptions.Item label="Went quiet">{ec?.quiet ?? 0}</Descriptions.Item>
              </Descriptions>
            </Section>

            <Section title="Pictures (pilot)">
              {img?.calls ? (
                <>
                  <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered>
                    <Descriptions.Item label="Pictures read">{img.calls}</Descriptions.Item>
                    <Descriptions.Item label="Confirmed by the customer">
                      {percentOf(img.acceptedPct, img.calls)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Had a chassis number">
                      {percentOf(img.withVinPct, img.calls)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Corrected afterwards">
                      {percentOf(img.correctedAfterwardsPct, img.accepted)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Chassis numbers that looked misread">
                      {img.vinCheckDigitFailed}
                    </Descriptions.Item>
                    <Descriptions.Item label="Total cost">
                      {formatUsd(img.costUsd)} ({img.callsThisMonth} of {img.monthlyCap} this
                      month)
                    </Descriptions.Item>
                  </Descriptions>
                  <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                    By stage:{' '}
                    {Object.entries(img.byStage || {})
                      .map(([k, n]) => `${k} ${n}`)
                      .join(' · ') || '—'}
                    {' · '}By kind:{' '}
                    {Object.entries(img.byKind || {})
                      .map(([k, n]) => `${k.replace(/_/g, ' ')} ${n}`)
                      .join(' · ') || '—'}
                  </Paragraph>
                </>
              ) : (
                <Note text={img?.note || 'No pictures read in this period.'} />
              )}
            </Section>

            <Section title="Car summary at payment">
              <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered>
                <Descriptions.Item label='"Change" used'>{changes?.opened ?? 0}</Descriptions.Item>
                <Descriptions.Item label="Detail changed">
                  {Object.entries(changes?.bySlot || {})
                    .map(([slot, n]) => `${slotLabel(slot)} ${n}`)
                    .join(' · ') || '—'}
                </Descriptions.Item>
              </Descriptions>
            </Section>

            <Section title="Conversion">
              <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered>
                <Descriptions.Item label="Paid, of those who sent a picture">
                  {percentOf(conv?.sentAPicturePaidPct, conv?.sentAPicture)}
                </Descriptions.Item>
                <Descriptions.Item label="Paid, of those who did not">
                  {percentOf(conv?.didNotSendAPicturePaidPct, conv?.didNotSendAPicture)}
                </Descriptions.Item>
              </Descriptions>
            </Section>

            {/*
              Where each service's customers came from (spec: assistant-front-door Req 3.1).
              Shown even when there is only duty: "vehicle history got nine" means nothing
              without "duty got a thousand" beside it, and a service that only ever arrives by
              somebody switching out of duty is a service whose own front door is not working.
            */}
            <Section title="Routing">
              {routes?.total ? (
                <>
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="key"
                    dataSource={routingRows(routes, serviceLabel)}
                    columns={[
                      { title: 'Service', dataIndex: 'service', key: 'service' },
                      {
                        title: 'Conversations',
                        key: 'total',
                        render: (_, row) => percentOf(row.sharePct, routes.total)
                      },
                      {
                        title: 'How they arrived',
                        key: 'signals',
                        render: (_, row) =>
                          row.signals
                            .map(sig => `${sig.label} ${sig.count}`)
                            .join(' · ') || '—'
                      }
                    ]}
                  />
                  <Descriptions
                    size="small"
                    column={{ xs: 1, md: 2 }}
                    bordered
                    style={{ marginTop: 12 }}
                  >
                    <Descriptions.Item label="Switches">
                      {outcomeLine(routes.switches, serviceLabel)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Hand-offs">
                      {outcomeLine(routes.handoffs, serviceLabel)}
                    </Descriptions.Item>
                  </Descriptions>
                </>
              ) : (
                <Note text="No conversations have been routed in this window. Until the services menu is on, every conversation is a duty check and nothing is recorded here." />
              )}
            </Section>

            {/*
              Cars across services (spec: 0040_assistant_car_decisions Req 5): how often a customer
              pointed at a car from another service, how it ended, and how many came back to a car
              they had already checked or bought.
            */}
            <Section title="Cars across services">
              {cd?.opened || cd?.alreadyDone ? (
                <>
                  <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered>
                    <Descriptions.Item label="Questions asked">{cd.opened}</Descriptions.Item>
                    <Descriptions.Item label="Answered">
                      {percentOf(cd.resolvedPct, cd.opened)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Started for">
                      {Object.entries(cd.byTarget || {})
                        .map(([key, n]) => `${serviceLabel(key)} ${n}`)
                        .join(' · ') || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Already had it">{cd.alreadyDone}</Descriptions.Item>
                  </Descriptions>
                  <Row gutter={16} style={{ marginTop: 12 }}>
                    {['resolved', 'lapsed'].map(group => (
                      <Col xs={24} md={12} key={group}>
                        <Table
                          size="small"
                          pagination={false}
                          rowKey="key"
                          dataSource={carDecisionRows(cd, group)}
                          locale={{ emptyText: group === 'resolved' ? 'None answered' : 'None lapsed' }}
                          columns={[
                            {
                              title: group === 'resolved' ? 'How it ended' : 'Why it lapsed',
                              dataIndex: 'label',
                              key: 'label'
                            },
                            { title: 'Count', dataIndex: 'count', key: 'count' }
                          ]}
                        />
                      </Col>
                    ))}
                  </Row>
                </>
              ) : (
                <Note text="No decisions in this period." />
              )}
            </Section>

            {/*
              Where customers stop, for a service whose middle step is "type seventeen
              characters". Each figure is a share of the step BEFORE it, so nothing reads as a
              share of something customers never reached — and the two identifier counts are kept
              apart, because "typed something we could not use" and "never typed anything" call
              for completely different fixes.
            */}
            <Section title="History reports on WhatsApp">
              {vh?.started ? (
                <>
                  <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered>
                    <Descriptions.Item label="Started">{vh.started}</Descriptions.Item>
                    <Descriptions.Item label="Chose a report">
                      {vh.productChosen}
                    </Descriptions.Item>
                    <Descriptions.Item label="Gave a usable number">
                      {percentOf(vh.validPct, vh.started)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Number we could not use">
                      {vh.identifierUnusable}
                    </Descriptions.Item>
                    <Descriptions.Item label="Paid">
                      {percentOf(vh.paidPct, vh.productChosen)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Confirmed after paying">
                      {percentOf(vh.confirmedPct, vh.paid)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Changed the number">
                      {vh.changedTheNumber}
                    </Descriptions.Item>
                    <Descriptions.Item label="Delivered">{vh.delivered}</Descriptions.Item>
                  </Descriptions>
                  <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                    Where the cars are from: {countLine(vh.byMarket, VH_MARKET_LABELS)}
                    {' · '}How they ended: {countLine(vh.byOutcome)}
                    {Object.keys(vh.failed || {}).length
                      ? ` · Could not sell or deliver: ${countLine(vh.failed)}`
                      : ''}
                  </Paragraph>
                </>
              ) : (
                <Note text={'No history report has been started in this window. Until the service is switched on, nothing is recorded here.'} />
              )}
            </Section>

            <Alert
              type="info"
              message="Targets for the 30-day review: pictures confirmed ≥ 80% · chassis numbers corrected ≤ 5% · ad arrivals replying up from 44% · final-check flags down from ~17% of finished chats."
            />
          </>
        )}
      </div>
    </div>
  )
}

PilotReport.propTypes = {
  onLoaded: PropTypes.func
}

PilotReport.defaultProps = {
  onLoaded: () => {}
}

export default PilotReport
